import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import DeleteForeverSharpIcon from "@mui/icons-material/DeleteForeverSharp";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import {
  pdf,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
} from "@react-pdf/renderer";
import * as XLSX from "xlsx";
import { api } from "../../hooks/apiClient";
import { useSnackbar } from "notistack";

// ── PDF definition ──────────────────────────────────────────────────────────
const pdfStyles = StyleSheet.create({
  page: { padding: 24, fontSize: 9, fontFamily: "Helvetica" },
  title: { fontSize: 14, fontWeight: "bold", marginBottom: 4 },
  meta: { fontSize: 9, marginBottom: 2, color: "#444" },
  metaRow: { flexDirection: "row", gap: 16, marginBottom: 10 },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#2980b9",
    color: "#fff",
    padding: "4 6",
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
    padding: "3 6",
    borderBottomWidth: 0.5,
    borderBottomColor: "#ddd",
  },
  altRow: { backgroundColor: "#f5f5f5" },
  col1: { width: "28%" },
  col2: { width: "14%" },
  col3: { width: "18%" },
  col4: { width: "18%" },
  col5: { width: "10%", textAlign: "right" },
  col6: { width: "12%", textAlign: "right" },
  sectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginTop: 8,
    marginBottom: 3,
    color: "#2980b9",
  },
});

const OrderPDF = ({ order }) => {
  const allVariants = order.groupedDetails.flatMap((p) =>
    p.variants.map((v) => ({ ...v, product_title: p.product_title })),
  );

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <Text style={pdfStyles.title}>DG Order #{order.id}</Text>
        <View style={pdfStyles.metaRow}>
          <Text style={pdfStyles.meta}>
            Status: {order.isDispatched ? "Dispatched" : order.status}
          </Text>
          <Text style={pdfStyles.meta}>Store ID: {order.store_id}</Text>
          <Text style={pdfStyles.meta}>Total Qty: {order.total_qty}</Text>
          <Text style={pdfStyles.meta}>
            Total Price: PKR {Number(order.total_price).toLocaleString()}
          </Text>
        </View>

        {/* Table header */}
        <View style={pdfStyles.tableHeader}>
          <Text style={pdfStyles.col1}>Product</Text>
          <Text style={pdfStyles.col2}>Variant</Text>
          <Text style={pdfStyles.col3}>SKU</Text>
          <Text style={pdfStyles.col4}>Barcode</Text>
          <Text style={pdfStyles.col5}>Qty</Text>
          <Text style={pdfStyles.col6}>Price</Text>
        </View>

        {allVariants.map((v, i) => (
          <View
            key={v.id}
            style={[pdfStyles.tableRow, i % 2 === 1 ? pdfStyles.altRow : {}]}
          >
            <Text style={pdfStyles.col1}>{v.product_title}</Text>
            <Text style={pdfStyles.col2}>{v.variant_title}</Text>
            <Text style={pdfStyles.col3}>{v.sku}</Text>
            <Text style={pdfStyles.col4}>{v.barcode}</Text>
            <Text style={pdfStyles.col5}>{v.quantity}</Text>
            <Text style={pdfStyles.col6}>
              {Number(v.price).toLocaleString()}
            </Text>
          </View>
        ))}
      </Page>
    </Document>
  );
};
// ────────────────────────────────────────────────────────────────────────────

function ProductRow({ product, deleteProduct }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{product.product_title}</TableCell>
        <TableCell align="right">{product.variants.length} variants</TableCell>
        <TableCell align="right">
          {product.variants.reduce((sum, v) => sum + v.quantity, 0)} pcs
        </TableCell>
        <TableCell align="right">
          <IconButton
            size="small"
            color="error"
            onClick={() => deleteProduct(product.product_id)}
          >
            <DeleteForeverSharpIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell>SKU</TableCell>
                    <TableCell>Barcode</TableCell>
                    <TableCell>Variant</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Price (PKR)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {product.variants.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell>{v.sku}</TableCell>
                      <TableCell>{v.barcode}</TableCell>
                      <TableCell>{v.variant_title}</TableCell>
                      <TableCell align="right">{v.quantity}</TableCell>
                      <TableCell align="right">
                        {Number(v.price).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const DGOrderDetail = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);

  const fetchOrder = async () => {
    setLoading(true);
    try {
      const response = await api.get(`orders/dg/find/${id}`);
      setOrderData(response.data);
    } catch (error) {
      console.error("Error fetching DG order:", error);
      enqueueSnackbar("Failed to load order", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const blob = await pdf(<OrderPDF order={orderData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `DG-Order-${orderData.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      enqueueSnackbar("Failed to generate PDF", { variant: "error" });
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadExcel = () => {
    const rows = orderData.groupedDetails.flatMap((product) =>
      product.variants.map((v) => ({
        Product: product.product_title,
        Variant: v.variant_title,
        SKU: v.sku,
        Barcode: v.barcode,
        Quantity: v.quantity,
        "Price (PKR)": Number(v.price),
      })),
    );
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Order-${orderData.id}`);
    XLSX.writeFile(wb, `DG-Order-${orderData.id}.xlsx`);
  };

  const handleDispatch = async () => {
    const dispatched = !orderData.isDispatched;
    const confirmed = window.confirm(
      dispatched ? "Dispatch this order?" : "Re-open this order?",
    );
    if (!confirmed) return;
    try {
      await api.put(`orders/dg/${orderData.id}`, {
        store_id: orderData.store_id,
        isDispatched: dispatched,
        status: dispatched ? "Dispatched" : "Opened again",
      });
      enqueueSnackbar(dispatched ? "Order dispatched" : "Order re-opened", {
        variant: "success",
      });
      fetchOrder();
    } catch (error) {
      enqueueSnackbar("Failed to update order", { variant: "error" });
    }
  };

  const deleteProduct = async (pid) => {
    const confirmed = window.confirm("Delete this product from the order?");
    if (!confirmed) return;
    try {
      await api.delete(`orders/dg/${orderData.id}`, {
        params: { product_id: pid },
      });
      enqueueSnackbar("Product removed from order", { variant: "warning" });
      fetchOrder();
    } catch (error) {
      enqueueSnackbar("Failed to delete product", { variant: "error" });
    }
  };

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (!orderData) return <Typography p={2}>No Order Found</Typography>;

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Button
          variant="contained"
          startIcon={orderData.isDispatched ? <EditIcon /> : <SaveIcon />}
          onClick={handleDispatch}
          color={orderData.isDispatched ? "warning" : "primary"}
        >
          {orderData.isDispatched ? "Re-open Order" : "Dispatch Order"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdfIcon />}
          onClick={downloadPDF}
          color="error"
          disabled={pdfLoading}
        >
          {pdfLoading ? "Generating..." : "Download PDF"}
        </Button>
        <Button
          variant="outlined"
          startIcon={<TableChartIcon />}
          onClick={downloadExcel}
          color="success"
        >
          Download Excel
        </Button>
        <Chip
          label={orderData.isDispatched ? "Dispatched" : orderData.status}
          color={orderData.isDispatched ? "success" : "default"}
        />
      </Box>

      <Box display="flex" gap={3} mb={2} flexWrap="wrap">
        <Typography variant="body2">
          Order # <strong>{orderData.id}</strong>
        </Typography>
        <Typography variant="body2">
          Store ID: <strong>{orderData.store_id}</strong>
        </Typography>
        <Typography variant="body2">
          Total Qty: <strong>{orderData.total_qty}</strong>
        </Typography>
        <Typography variant="body2">
          Total Price:{" "}
          <strong>PKR {Number(orderData.total_price).toLocaleString()}</strong>
        </Typography>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
              <TableCell />
              <TableCell>Product</TableCell>
              <TableCell align="right">Variants</TableCell>
              <TableCell align="right">Total Qty</TableCell>
              <TableCell align="right">Delete</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderData.groupedDetails?.map((product) => (
              <ProductRow
                key={product.product_id}
                product={product}
                deleteProduct={deleteProduct}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DGOrderDetail;
